import { FC, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Save,
  Cancel,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledAutocomplete from "../../components/controlledComponents/ControlledAutocomplete";
import {
  TGradeCriteria,
  TAssessmentRubric,
  TAssessmentType,
} from "../../../apis/assessment/assessment.interfaces";
import {
  useCreateGradeCriteriaHook,
  useUpdateGradeCriteriaHook,
  useGradeCriteriaHook,
} from "../../../hooks/useAssessmentHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { useGradeLevelHook } from "../../../hooks/useAcademicHook";

const rubricSchema = z.object({
  assessmentType: z.enum([
    "continuous-assessment",
    "term-exams",
    "project-based",
    "practical",
    "oral",
    "written",
    "other",
  ]),
  criteria: z
    .string()
    .min(1, "Criteria is required")
    .max(500, "Criteria must not exceed 500 characters"),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
});

const gradeCriteriaSchema = z
  .object({
    gradeLevelId: z
      .object({
        id: z.string(),
        label: z.string(),
      })
      .refine((val) => val && val.id, {
        message: "Please select a grade level",
      }),
    minMarks: z.coerce.number().min(0, "Minimum marks must be at least 0"),
    maxMarks: z
      .coerce.number()
      .min(0, "Maximum marks must be at least 0")
      .optional()
      .nullable(),
    description: z
      .string()
      .max(1000, "Description must not exceed 1000 characters")
      .optional()
      .or(z.literal("")),
    performanceLevel: z
      .string()
      .max(100, "Performance level must not exceed 100 characters")
      .optional()
      .or(z.literal("")),
    rubrics: z.array(rubricSchema).optional(),
  })
  .refine(
    (data) => {
      if (data.maxMarks !== null && data.maxMarks !== undefined) {
        return data.maxMarks >= data.minMarks;
      }
      return true;
    },
    {
      message: "Maximum marks must be greater than or equal to minimum marks",
      path: ["maxMarks"],
    },
  );

type GradeCriteriaFormData = z.infer<typeof gradeCriteriaSchema>;

interface ManageGradeCriteriaDialogProps {
  open: boolean;
  onClose: () => void;
  gradeCriteria?: TGradeCriteria | null;
  gradeLevelId?: string; // Pre-select a grade level if provided
}

const ManageGradeCriteriaDialog: FC<ManageGradeCriteriaDialogProps> = ({
  open,
  onClose,
  gradeCriteria,
  gradeLevelId,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createCriteriaMutation = useCreateGradeCriteriaHook();
  const updateCriteriaMutation = useUpdateGradeCriteriaHook();
  const { data: gradeLevels } = useGradeLevelHook();
  const { data: existingCriteria } = useGradeCriteriaHook();
  const criteriaList = useMemo(
    (): TGradeCriteria[] => existingCriteria ?? [],
    [existingCriteria],
  );
  const isEditMode = !!gradeCriteria;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<GradeCriteriaFormData>({
    resolver: zodResolver(gradeCriteriaSchema),
    defaultValues: {
      gradeLevelId: { id: "", label: "" },
      minMarks: 0,
      maxMarks: undefined,
      description: "",
      performanceLevel: "",
      rubrics: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "rubrics",
  });

  const watchedMinMarks = watch("minMarks");
  const watchedMaxMarks = watch("maxMarks");

  const gradeLevelOptions = useMemo(() => {
    return (
      gradeLevels?.map((gl) => ({
        id: gl.id,
        label: gl.name,
      })) || []
    );
  }, [gradeLevels]);

  const assessmentTypeOptions: { key: TAssessmentType; value: string }[] = [
    { key: "continuous-assessment", value: "Continuous Assessment" },
    { key: "term-exams", value: "Term Exams" },
    { key: "project-based", value: "Project-Based" },
    { key: "practical", value: "Practical" },
    { key: "oral", value: "Oral" },
    { key: "written", value: "Written" },
    { key: "other", value: "Other" },
  ];

  // Validate grade boundaries don't overlap
  useEffect(() => {
    if (
      watchedMinMarks !== undefined &&
      watchedMaxMarks !== undefined &&
      criteriaList.length > 0 &&
      !isEditMode
    ) {
      const overlapping = criteriaList.some((criteria) => {
        const criteriaMin = criteria.minMarks;
        const criteriaMax = criteria.maxMarks ?? Infinity;
        return (
          (watchedMinMarks !== null &&
            watchedMinMarks !== undefined &&
            watchedMinMarks >= criteriaMin &&
            watchedMinMarks <= criteriaMax) ||
          (watchedMaxMarks !== null &&
            watchedMaxMarks !== undefined &&
            watchedMaxMarks >= criteriaMin &&
            watchedMaxMarks <= criteriaMax) ||
          (watchedMinMarks !== null &&
            watchedMinMarks !== undefined &&
            watchedMaxMarks !== null &&
            watchedMaxMarks !== undefined &&
            watchedMinMarks <= criteriaMin &&
            watchedMaxMarks >= criteriaMax)
        );
      });
      if (overlapping) {
        // This will be caught by form validation
      }
    }
  }, [
    watchedMinMarks,
    watchedMaxMarks,
    criteriaList,
    isEditMode,
  ]);

  useEffect(() => {
    if (gradeCriteria && gradeLevels) {
      const selectedGradeLevel = gradeLevels.find(
        (gl) => gl.id === gradeCriteria.gradeLevelId,
      );

      reset({
        gradeLevelId: selectedGradeLevel
          ? { id: selectedGradeLevel.id, label: selectedGradeLevel.name }
          : { id: "", label: "" },
        minMarks: gradeCriteria.minMarks,
        maxMarks: gradeCriteria.maxMarks ?? undefined,
        description: gradeCriteria.description || "",
        performanceLevel: gradeCriteria.performanceLevel || "",
        rubrics:
          gradeCriteria.rubrics?.map((rubric) => ({
            assessmentType: rubric.assessmentType,
            criteria: rubric.criteria,
            description: rubric.description || "",
          })) || [],
      });
    } else if (gradeLevelId && gradeLevels) {
      const selectedGradeLevel = gradeLevels.find(
        (gl) => gl.id === gradeLevelId,
      );
      reset({
        gradeLevelId: selectedGradeLevel
          ? { id: selectedGradeLevel.id, label: selectedGradeLevel.name }
          : { id: "", label: "" },
        minMarks: 0,
        maxMarks: undefined,
        description: "",
        performanceLevel: "",
        rubrics: [],
      });
    } else {
      reset({
        gradeLevelId: { id: "", label: "" },
        minMarks: 0,
        maxMarks: undefined,
        description: "",
        performanceLevel: "",
        rubrics: [],
      });
    }
  }, [gradeCriteria, reset, open, gradeLevels, gradeLevelId]);

  const onSubmit = async (data: GradeCriteriaFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    // Check for overlapping grade boundaries
    if (
      criteriaList.length > 0 &&
      data.maxMarks !== null &&
      data.maxMarks !== undefined
    ) {
      const overlapping = criteriaList.some((criteria: TGradeCriteria) => {
        if (isEditMode && criteria.id === gradeCriteria?.id) return false;
        const criteriaMin = criteria.minMarks;
        const criteriaMax = criteria.maxMarks ?? Infinity;
        return (
          (data.minMarks !== null &&
            data.minMarks !== undefined &&
            data.minMarks >= criteriaMin &&
            data.minMarks <= criteriaMax) ||
          (data.maxMarks !== null &&
            data.maxMarks !== undefined &&
            data.maxMarks >= criteriaMin &&
            data.maxMarks <= criteriaMax) ||
          (data.minMarks !== null &&
            data.minMarks !== undefined &&
            data.maxMarks !== null &&
            data.maxMarks !== undefined &&
            data.minMarks <= criteriaMin &&
            data.maxMarks >= criteriaMax)
        );
      });

      if (overlapping) {
        logger.show_error(
          "Grade boundaries overlap with existing criteria. Please adjust the marks range.",
        );
        return;
      }
    }

    try {
      const rubrics: Omit<TAssessmentRubric, "id">[] | undefined =
        data.rubrics && data.rubrics.length > 0
          ? data.rubrics.map((rubric) => ({
            assessmentType: rubric.assessmentType,
            criteria: rubric.criteria,
            description: rubric.description,
          }))
          : undefined;

      if (isEditMode && gradeCriteria) {
        await updateCriteriaMutation.mutateAsync({
          id: gradeCriteria.id,
          request: {
            minMarks: data.minMarks,
            maxMarks: data.maxMarks ?? undefined,
            description: data.description || undefined,
            performanceLevel: data.performanceLevel || undefined,
            rubrics,
          },
        });
        logger.show_success("Grade criteria updated successfully!");
      } else {
        await createCriteriaMutation.mutateAsync({
          gradeLevelId: data.gradeLevelId.id,
          minMarks: data.minMarks,
          maxMarks: data.maxMarks ?? undefined,
          description: data.description || undefined,
          performanceLevel: data.performanceLevel || undefined,
          rubrics,
          instituteId: selectedInstitute.id,
        });
        logger.show_success("Grade criteria created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} grade criteria. Please try again.`,
      );
    }
  };

  const handleAddRubric = () => {
    append({
      assessmentType: "written",
      criteria: "",
      description: "",
    });
  };

  const handleRemoveRubric = (index: number) => {
    remove(index);
  };

  const isLoading =
    isSubmitting ||
    createCriteriaMutation.isPending ||
    updateCriteriaMutation.isPending;
  const isError =
    createCriteriaMutation.isError || updateCriteriaMutation.isError;
  const errorMessage =
    createCriteriaMutation.error?.message ||
    updateCriteriaMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-grade-criteria-dialog"
    >
      <DialogTitle data-testid="manage-grade-criteria-dialog-title">
        {isEditMode ? "Edit Grade Criteria" : "Configure Grade Criteria"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="grade-criteria-form">
        <DialogContent data-testid="grade-criteria-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="grade-criteria-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} grade criteria. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="grade-criteria-form-container"
          >
            <ControlledAutocomplete
              control={control}
              name="gradeLevelId"
              id="grade-criteria-grade-level"
              label="Grade Level"
              dataOptions={gradeLevelOptions}
              errorMessage={errors.gradeLevelId?.message}
              dataTestId="grade-criteria-grade-level-input"
            />

            <ControlledTextField
              control={control}
              name="minMarks"
              id="grade-criteria-min-marks"
              label="Minimum Marks"
              type="number"
              required
              errorMessage={errors.minMarks?.message}
              inputProps={{
                "data-testid": "grade-criteria-min-marks-input",
                min: 0,
              }}
            />

            <ControlledTextField
              control={control}
              name="maxMarks"
              id="grade-criteria-max-marks"
              label="Maximum Marks (Optional)"
              type="number"
              errorMessage={errors.maxMarks?.message}
              inputProps={{
                "data-testid": "grade-criteria-max-marks-input",
                min: 0,
              }}
            />

            <ControlledTextField
              control={control}
              name="performanceLevel"
              id="grade-criteria-performance-level"
              label="Performance Level"
              errorMessage={errors.performanceLevel?.message}
              inputProps={{
                "data-testid": "grade-criteria-performance-level-input",
              }}
            />

            <ControlledTextField
              control={control}
              name="description"
              id="grade-criteria-description"
              label="Description"
              multiline
              rows={3}
              errorMessage={errors.description?.message}
              inputProps={{ "data-testid": "grade-criteria-description-input" }}
            />

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h6"
                  data-testid="grade-criteria-rubrics-title"
                >
                  Assessment Rubrics (Optional)
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddRubric}
                  data-testid="add-rubric-button"
                >
                  Add Rubric
                </Button>
              </Box>

              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                  data-testid={`rubric-${index}`}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <ControlledSelectField
                        control={control}
                        name={`rubrics.${index}.assessmentType`}
                        id={`rubric-type-${index}`}
                        label="Assessment Type"
                        itemList={assessmentTypeOptions}
                        isRequired
                        errorMessage={
                          errors.rubrics?.[index]?.assessmentType?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <ControlledTextField
                        control={control}
                        name={`rubrics.${index}.criteria`}
                        id={`rubric-criteria-${index}`}
                        label="Criteria"
                        required
                        errorMessage={
                          errors.rubrics?.[index]?.criteria?.message
                        }
                        inputProps={{
                          "data-testid": `rubric-criteria-input-${index}`,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Box display="flex" justifyContent="flex-end">
                        <IconButton
                          onClick={() => handleRemoveRubric(index)}
                          color="error"
                          size="small"
                          data-testid={`remove-rubric-button-${index}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name={`rubrics.${index}.description`}
                        id={`rubric-description-${index}`}
                        label="Description"
                        multiline
                        rows={2}
                        errorMessage={
                          errors.rubrics?.[index]?.description?.message
                        }
                        inputProps={{
                          "data-testid": `rubric-description-input-${index}`,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {fields.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No rubrics added. Click "Add Rubric" to define assessment
                  rubrics for this grade level.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions data-testid="grade-criteria-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-grade-criteria-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-grade-criteria-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Criteria"
                : "Create Criteria"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageGradeCriteriaDialog;
